// frontend/src/pages/WalkInRegistration.js
// Register a walk-in client + their vehicle, or attach a vehicle to an existing
// client. Chrome/ink design, three steps: search → register → success.

import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import VinInput, { isVinFormatValid } from '../components/VinInput';
import SearchableSelect from '../components/SearchableSelect';
import CompanySelect, { TECH_CHECK_COMPANIES } from '../components/CompanySelect';
import { trackPixelCustom } from '../utils/metaPixel';
import API_URL from '../api/baseURL';
import {
  Page, Panel, Button, Field, ErrorNote, Note,
  inputClass, selectClass,
} from '../components/ui/Iparc';

const FUEL_TYPES = ['Essence', 'Gasoil', 'Diesel', 'Hybrid', 'Hybride', 'Electric', 'GPL'];

const EMPTY_FORM = {
  contactName: '', phoneNumber: '', email: '',
  plateNumber: '', vin: '', brand: '', model: '', year: '',
  odometerReading: '', nextOilChangeOdometer: '', fuelType: 'Essence',
  insuranceCompany: '', insuranceExpiry: '', technicalCheckCompany: '', technicalCheckExpiry: '',
};

export default function WalkInRegistration() {
  const { token } = useAuth();
  const navigate = useNavigate();

  /* ─── State ─── */
  const [step, setStep] = useState('search'); // search | register | success
  const [plateSearch, setPlateSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  // OTP link-consent flow state
  const [linkStep, setLinkStep] = useState('idle'); // idle | sent
  const [otpInput, setOtpInput] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [resendIn, setResendIn] = useState(0); // seconds remaining

  const [form, setForm] = useState(EMPTY_FORM);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  // Existing-client mode: pick a client from a dropdown and add a vehicle to them
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Fetch this workshop's existing clients (incl. ghosts they created)
  const fetchClients = async () => {
    setClientsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/users?role=car owner`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Impossible de charger la liste des clients');
    } finally {
      setClientsLoading(false);
    }
  };

  // Enter existing-client mode from the search screen
  const startExistingClientMode = () => {
    setMode('existing');
    setSelectedClientId('');
    setError('');
    setStep('register');
    fetchClients();
  };

  // Reset everything back to the initial search screen
  const resetAll = () => {
    setStep('search');
    setMode('new');
    setPlateSearch('');
    setSearchResult(null);
    setResult(null);
    setError('');
    setSelectedClientId('');
    setLinkStep('idle');
    setOtpInput('');
    setLinkError('');
    setResendIn(0);
    setForm(EMPTY_FORM);
  };

  /* ─── Step 1: Search by plate ─── */
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!plateSearch.trim()) return;
    setSearching(true);
    setError('');
    setSearchResult(null);

    try {
      const res = await axios.get(`${API_URL}/vehicles/search`, {
        params: { plate: plateSearch.trim().toUpperCase() },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.found) {
        setSearchResult(res.data);
      } else {
        // Not found — go to registration form with plate pre-filled
        setForm(prev => ({ ...prev, plateNumber: plateSearch.trim().toUpperCase() }));
        setStep('register');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'La recherche a échoué');
    } finally {
      setSearching(false);
    }
  };

  const startResendCooldown = (secs) => {
    setResendIn(secs);
    const timer = setInterval(() => {
      setResendIn((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleRequestLink = async (vehicleId) => {
    setLinkBusy(true);
    setLinkError('');
    try {
      await axios.post(`${API_URL}/vehicles/${vehicleId}/request-link`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLinkStep('sent');
      startResendCooldown(60);
    } catch (err) {
      setLinkError(err.response?.data?.error || "Échec de l'envoi du code");
    } finally {
      setLinkBusy(false);
    }
  };

  const handleVerifyLink = async (vehicleId) => {
    if (!otpInput.trim()) { setLinkError('Entrez le code reçu par le client'); return; }
    setLinkBusy(true);
    setLinkError('');
    try {
      await axios.post(`${API_URL}/vehicles/${vehicleId}/verify-link`,
        { otp: otpInput.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      setResult({ linked: true, vehicleId });
      setStep('success');
    } catch (err) {
      setLinkError(err.response?.data?.error || 'Code invalide');
    } finally {
      setLinkBusy(false);
    }
  };

  const handleResendLink = async (vehicleId) => {
    if (resendIn > 0) return;
    setLinkBusy(true);
    setLinkError('');
    try {
      await axios.post(`${API_URL}/vehicles/${vehicleId}/resend-link`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      startResendCooldown(60);
    } catch (err) {
      const retry = err.response?.data?.retryAfter;
      if (retry) startResendCooldown(retry);
      setLinkError(err.response?.data?.error || 'Échec du renvoi');
    } finally {
      setLinkBusy(false);
    }
  };

  /* ─── Step 2: Register ghost + vehicle ─── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client validation depends on mode
    if (mode === 'existing') {
      if (!selectedClientId) return setError('Veuillez sélectionner un client');
    } else {
      if (!form.contactName.trim()) return setError('Le nom du client est requis');
      if (!form.phoneNumber.trim()) return setError('Le numéro de téléphone est requis');
    }
    // Shared vehicle validation
    if (!form.plateNumber.trim()) return setError('Le numéro de plaque est requis');
    if (form.vin && !isVinFormatValid(form.vin)) return setError('Le VIN doit contenir exactement 17 caractères valides');
    if (!form.insuranceCompany || !form.insuranceExpiry) return setError("Les informations d'assurance sont requises");
    if (!form.technicalCheckCompany || !form.technicalCheckExpiry) return setError('Les informations du contrôle technique sont requises');

    // Vehicle fields shared by both modes
    const vehiclePayload = {
      plateNumber: form.plateNumber.trim().toUpperCase(),
      vin: form.vin.trim().toUpperCase() || undefined,
      brand: form.brand.trim(),
      model: form.model.trim(),
      year: form.year ? String(form.year) : undefined,
      odometerReading: Number(form.odometerReading) || 0,
      nextOilChangeOdometer: Number(form.nextOilChangeOdometer) || undefined,
      fuelType: form.fuelType,
      insuranceCompany: form.insuranceCompany.trim(),
      insuranceExpiry: form.insuranceExpiry ? new Date(form.insuranceExpiry).toISOString() : undefined,
      technicalCheckCompany: form.technicalCheckCompany.trim(),
      technicalCheckExpiry: form.technicalCheckExpiry ? new Date(form.technicalCheckExpiry).toISOString() : undefined,
    };

    setSubmitting(true);
    try {
      let res;
      if (mode === 'existing') {
        res = await axios.post(`${API_URL}/vehicles/for-client/${selectedClientId}`, vehiclePayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        res = await axios.post(`${API_URL}/vehicles/walk-in`, {
          ...vehiclePayload,
          contactName: form.contactName.trim(),
          phoneNumber: form.phoneNumber.trim(),
          email: form.email.trim() || undefined,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      // Meta Pixel: new walk-in account vs. a car added to an existing client
      if (mode === 'existing') trackPixelCustom('VehicleAdded', { source: 'walk-in-existing' });
      else trackPixelCustom('WalkinRegistered');

      setResult(res.data);
      setStep('success');
    } catch (err) {
      setError(err.response?.data?.error || "L'enregistrement a échoué");
    } finally {
      setSubmitting(false);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════ */

  // ─── SUCCESS ───
  if (step === 'success') {
    return (
      <Page title="Enregistrement terminé" subtitle="Client sans rendez-vous" width="narrow">
        <Panel>
          <div className="flex flex-col items-center text-center gap-3 py-10">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(116,169,141,.13)' }}
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#74A98D"
                   strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <h2 className="font-['Space_Grotesk',sans-serif] text-[19px] font-semibold tracking-[-.015em]">
              {result?.linked
                ? 'Véhicule lié avec succès'
                : result?.isNew
                  ? 'Client et véhicule enregistrés'
                  : 'Véhicule existant lié'}
            </h2>
            {result?.ownerIsNew && (
              <p className="text-[12.5px] text-[#8F877B] max-w-[420px]">
                Un compte a été créé pour le client. Il recevra un e-mail pour l'activer.
              </p>
            )}
            <div className="flex flex-wrap gap-2.5 justify-center pt-2">
              <Button variant="primary" onClick={() => navigate('/workshop/reception-checklist')}>
                Fiche de réception
              </Button>
              <Button onClick={resetAll}>Nouveau client</Button>
            </div>
          </div>
        </Panel>
      </Page>
    );
  }

  // ─── SEARCH ───
  if (step === 'search') {
    return (
      <Page
        title="Client sans rendez-vous"
        subtitle="Atelier"
        width="narrow"
      >
        <p className="text-[12.5px] text-[#8F877B] -mt-2">
          Recherchez le véhicule par sa plaque. S'il n'existe pas, enregistrez le client et son véhicule.
        </p>

        <Panel title="Rechercher un véhicule">
          <form onSubmit={handleSearch} className="flex flex-col gap-3">
            <Field label="Numéro de plaque">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={plateSearch}
                  onChange={(e) => setPlateSearch(e.target.value.toUpperCase())}
                  placeholder="Ex : AB1234CI"
                  className={`${inputClass} font-['Space_Grotesk',sans-serif] tracking-[.04em]`}
                  autoFocus
                />
                <Button
                  as="button"
                  type="submit"
                  variant="primary"
                  disabled={searching || !plateSearch.trim()}
                  className="h-[38px]"
                >
                  {searching ? '…' : 'Chercher'}
                </Button>
              </div>
            </Field>
          </form>

          {error && <div className="mt-3"><ErrorNote>{error}</ErrorNote></div>}

          {/* Search result: vehicle found */}
          {searchResult && (
            <div className="mt-4 rounded-xl border border-[#2B2724] p-4 flex flex-col gap-3">
              <p className="text-[10px] uppercase tracking-[.18em] text-[#74A98D]">Véhicule trouvé</p>
              <div className="text-[12.5px] text-[#C9C3B6] flex flex-col gap-1">
                <p>
                  <span className="text-[#6E665C]">Plaque : </span>
                  <span className="font-['Space_Grotesk',sans-serif] tracking-[.02em] text-[#F4F1EA]">
                    {searchResult.vehicle.plateNumber}
                  </span>
                </p>
                <p>
                  <span className="text-[#6E665C]">Véhicule : </span>
                  {searchResult.vehicle.brand} {searchResult.vehicle.model} {searchResult.vehicle.year}
                </p>
                {searchResult.owner && (
                  <p>
                    <span className="text-[#6E665C]">Propriétaire : </span>
                    {searchResult.owner.name} — {searchResult.owner.phoneNumber}
                  </p>
                )}
              </div>

              {searchResult.relationship === 'own' && (
                <>
                  <Note tone="ok">Ce véhicule est déjà lié à votre atelier.</Note>
                  <div>
                    <Button variant="primary" onClick={() => navigate('/workshop/reception-checklist')}>
                      Aller à la réception
                    </Button>
                  </div>
                </>
              )}

              {searchResult.relationship === 'other' && (
                <div className="flex flex-col gap-3">
                  <Note tone="warn">
                    Ce véhicule appartient à un autre compte. Le propriétaire doit approuver l'accès
                    via un code SMS.
                  </Note>

                  {linkStep === 'idle' && (
                    <div>
                      <Button
                        variant="primary"
                        onClick={() => handleRequestLink(searchResult.vehicle._id)}
                        disabled={linkBusy}
                      >
                        {linkBusy ? 'Envoi…' : "Demander l'accès"}
                      </Button>
                    </div>
                  )}

                  {linkStep === 'sent' && (
                    <div className="flex flex-col gap-2">
                      <p className="text-[11.5px] text-[#8F877B]">
                        Un code a été envoyé au propriétaire. Demandez-lui le code à 6 chiffres et
                        saisissez-le ci-dessous.
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={otpInput}
                          onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ''))}
                          placeholder="123456"
                          className={`${inputClass} font-['Space_Grotesk',sans-serif] tracking-[.3em]`}
                        />
                        <Button
                          variant="primary"
                          className="h-[38px]"
                          onClick={() => handleVerifyLink(searchResult.vehicle._id)}
                          disabled={linkBusy}
                        >
                          {linkBusy ? '…' : 'Valider'}
                        </Button>
                      </div>
                      <button
                        onClick={() => handleResendLink(searchResult.vehicle._id)}
                        disabled={resendIn > 0 || linkBusy}
                        className="self-start text-[11.5px] text-[#6E665C] hover:text-[#C9C3B6] disabled:opacity-50 transition-colors"
                      >
                        {resendIn > 0 ? `Renvoyer le code (${resendIn} s)` : 'Renvoyer le code'}
                      </button>
                    </div>
                  )}

                  {linkError && <ErrorNote>{linkError}</ErrorNote>}
                </div>
              )}
            </div>
          )}
        </Panel>

        <Panel title="Ou enregistrer directement">
          <div className="flex flex-col sm:flex-row gap-2.5">
            <Button
              className="flex-1"
              onClick={() => {
                setMode('new');
                setForm(prev => ({ ...prev, plateNumber: plateSearch.trim().toUpperCase() }));
                setStep('register');
                setError('');
              }}
            >
              Nouveau client
            </Button>
            <Button className="flex-1" onClick={startExistingClientMode}>
              Véhicule pour un client existant
            </Button>
          </div>
        </Panel>
      </Page>
    );
  }

  // ─── REGISTER FORM ───
  return (
    <Page
      title={mode === 'existing' ? 'Nouveau véhicule' : 'Nouveau client'}
      subtitle="Client sans rendez-vous"
      width="narrow"
    >
      <p className="text-[12.5px] text-[#8F877B] -mt-2">
        {mode === 'existing'
          ? 'Sélectionnez le client, puis renseignez les informations de son nouveau véhicule.'
          : "Remplissez les informations du client et de son véhicule. Un compte sera créé automatiquement."}
      </p>

      {error && <ErrorNote>{error}</ErrorNote>}

      <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
        {/* ── Client section — dropdown (existing) or free-text (new) ── */}
        <Panel title={mode === 'existing' ? 'Client' : 'Informations du client'}>
          {mode === 'existing' ? (
            <Field
              label="Sélectionner le client"
              required
              hint="Seuls les clients déjà rattachés à votre atelier apparaissent ici."
            >
              <SearchableSelect
                options={clients.map(c => ({
                  value: c._id,
                  label: `${c.name || 'Client'}${c.phoneNumber ? ` — ${c.phoneNumber}` : ''}`,
                }))}
                value={selectedClientId}
                onChange={(val) => setSelectedClientId(val)}
                placeholder={clientsLoading ? 'Chargement des clients…' : 'Rechercher un client…'}
                emptyLabel={
                  clientsLoading
                    ? 'Chargement…'
                    : "Aucun client rattaché à votre atelier. Enregistrez-le d'abord comme nouveau client."
                }
              />
              {!clientsLoading && clients.length > 0 && (
                <span className="block text-[10.5px] text-[#5C554C] mt-1">
                  {clients.length} client{clients.length > 1 ? 's' : ''} · la recherche ignore
                  les accents (tapez « kone » pour « Koné »)
                </span>
              )}
            </Field>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Nom complet" required>
                <input type="text" name="contactName" value={form.contactName} onChange={handleChange}
                       className={inputClass} placeholder="Ex : Koné Amadou" required />
              </Field>
              <Field label="Téléphone" required>
                <input type="tel" name="phoneNumber" value={form.phoneNumber} onChange={handleChange}
                       className={inputClass} placeholder="Ex : +2250701234567" required />
              </Field>
              <Field
                label="E-mail (optionnel)"
                className="sm:col-span-2"
                hint="Si fourni, le client recevra un e-mail pour activer son compte."
              >
                <input type="email" name="email" value={form.email} onChange={handleChange}
                       className={inputClass} placeholder="Ex : client@email.com" />
              </Field>
            </div>
          )}
        </Panel>

        {/* ── Vehicle Info (same fields as AddVehicle) ── */}
        <Panel title="Informations du véhicule">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <Field label="Plaque d'immatriculation" required>
              <input
                type="text" name="plateNumber" value={form.plateNumber}
                onChange={(e) => setForm(prev => ({ ...prev, plateNumber: e.target.value.toUpperCase() }))}
                className={`${inputClass} font-['Space_Grotesk',sans-serif] tracking-[.04em]`}
                placeholder="Ex : AB1234CI" required
              />
            </Field>
            <Field label="VIN (châssis)">
              <VinInput
                value={form.vin}
                onChange={(e) => setForm(prev => ({ ...prev, vin: e.target.value }))}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mt-3.5">
            <Field label="Marque">
              <input type="text" name="brand" value={form.brand} onChange={handleChange}
                     className={inputClass} placeholder="Ex : Toyota" />
            </Field>
            <Field label="Modèle">
              <input type="text" name="model" value={form.model} onChange={handleChange}
                     className={inputClass} placeholder="Ex : Corolla" />
            </Field>
            <Field label="Année">
              <input type="number" name="year" value={form.year} onChange={handleChange}
                     className={inputClass} placeholder="Ex : 2020" min="1900" max="2030" />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mt-3.5">
            <Field label="Kilométrage">
              <input type="number" name="odometerReading" value={form.odometerReading} onChange={handleChange}
                     className={inputClass} placeholder="Ex : 45000" min="0" />
            </Field>
            <Field label="Prochaine vidange (km)">
              <input type="number" name="nextOilChangeOdometer" value={form.nextOilChangeOdometer}
                     onChange={handleChange} className={inputClass} placeholder="Ex : 50000" min="0" />
            </Field>
            <Field label="Carburant">
              <select name="fuelType" value={form.fuelType} onChange={handleChange} className={selectClass}>
                {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
          </div>
        </Panel>

        {/* ── Insurance & Technical Check (same as AddVehicle) ── */}
        <Panel title="Assurance et contrôle technique">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <Field label="Compagnie d'assurance" required>
              {/* Free text for now — switch back to a dropdown once the full list
                  of market insurers is gathered. */}
              <input
                type="text" name="insuranceCompany" value={form.insuranceCompany}
                onChange={handleChange} className={inputClass}
                placeholder="Nom de la compagnie d'assurance"
              />
            </Field>
            <Field label="Expiration assurance" required>
              <input type="date" name="insuranceExpiry" value={form.insuranceExpiry}
                     onChange={handleChange} className={inputClass} required />
            </Field>
            <Field label="Centre de contrôle technique" required>
              <CompanySelect
                value={form.technicalCheckCompany}
                onChange={(v) => setForm(prev => ({ ...prev, technicalCheckCompany: v }))}
                options={TECH_CHECK_COMPANIES}
                className={selectClass}
                inputClassName={inputClass}
                placeholder="Sélectionner un centre"
              />
            </Field>
            <Field label="Expiration contrôle technique" required>
              <input type="date" name="technicalCheckExpiry" value={form.technicalCheckExpiry}
                     onChange={handleChange} className={inputClass} required />
            </Field>
          </div>
        </Panel>

        {/* ── Actions ── */}
        <div className="flex gap-2.5">
          <Button
            as="button"
            type="button"
            className="flex-1"
            onClick={() => { setStep('search'); setError(''); setMode('new'); setSelectedClientId(''); }}
          >
            Retour
          </Button>
          <Button as="button" type="submit" variant="primary" className="flex-1" disabled={submitting}>
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </form>
    </Page>
  );
}
